import { apiClient, unwrap } from './client';
import type {
  ApiEnvelope,
  Entity360,
  EntityTimeline,
  EvidenceList,
  EvidenceView,
  TimelineKind,
  UboResult,
} from './types';

/**
 * Entity-graph reads for the entity-360 workbench (F-IS-02). `/entities/{id}` + `/ubo` are the
 * PS-1 endpoints (in the OpenAPI); `?asOf=` is the bitemporal point-in-time coordinate ("what did
 * we know on date X"). `/timeline` + `/evidence` are the PS-3 cross-track additions Track A is
 * building; the SPA degrades to teaching empty states (errors.ts `isNotAvailable`) when absent.
 */

const enc = (s: string) => encodeURIComponent(s);

/** GET /v1/entities/{id}?asOf= — the entity-360 view at now, or at a point in time. */
export async function getEntity360(id: string, asOf?: string): Promise<Entity360> {
  const { data } = await apiClient.get<ApiEnvelope<Entity360>>(`/v1/entities/${enc(id)}`, {
    params: { asOf },
  });
  return unwrap(data);
}

export interface EvidenceFile {
  /** Its `type` is what the server served the file as, which is never a type it could not confirm. */
  blob: Blob;
  fileName: string;
}

/**
 * GET /v1/entities/{id}/evidence/{hash}/content (OL-26 slice 4). The stored file behind a record. Each call
 * is one audited open on the server. A refusal arrives as a Blob here, so its JSON is read back for
 * `normalizeApiError`.
 */
export async function getEvidenceFile(id: string, contentHash: string, purpose: 'PREVIEW' | 'DOWNLOAD'): Promise<EvidenceFile> {
  try {
    const response = await apiClient.get<Blob>(`/v1/entities/${enc(id)}/evidence/${enc(contentHash)}/content`, {
      params: { purpose },
      responseType: 'blob',
      timeout: 60_000,
      headers: { Accept: '*/*' },
    });
    const name = /filename="([^"]+)"/.exec(String(response.headers['content-disposition'] ?? ''))?.[1];
    return { blob: response.data, fileName: name ?? 'document.bin' };
  } catch (e) {
    const response = (e as { response?: { data?: unknown } }).response;
    if (response?.data instanceof Blob) {
      const text = await response.data.text();
      try {
        response.data = JSON.parse(text);
      } catch {
        response.data = undefined;
      }
    }
    throw e;
  }
}

export interface UboQuery {
  asOf?: string;
  edgeTypes?: string;
  maxDepth?: number;
}

/** GET /v1/entities/{id}/ubo?asOf= — ownership traversal (effective fractions, ≤ 4 hops). */
export async function getEntityUbo(id: string, q: UboQuery = {}): Promise<UboResult> {
  const { data } = await apiClient.get<ApiEnvelope<UboResult>>(`/v1/entities/${enc(id)}/ubo`, {
    params: { asOf: q.asOf, edgeTypes: q.edgeTypes, maxDepth: q.maxDepth },
  });
  return unwrap(data);
}

export interface TimelineQuery {
  from?: string;
  to?: string;
  kinds?: TimelineKind[];
  page?: number;
  pageSize?: number;
}

/** GET /v1/entities/{id}/timeline — the bitemporal event ledger, newest-first, kind-filterable. */
export async function getEntityTimeline(
  id: string,
  q: TimelineQuery = {},
): Promise<EntityTimeline> {
  const { data } = await apiClient.get<ApiEnvelope<EntityTimeline>>(
    `/v1/entities/${enc(id)}/timeline`,
    {
      params: {
        from: q.from,
        to: q.to,
        kinds: q.kinds?.length ? q.kinds.join(',') : undefined,
        page: q.page,
        pageSize: q.pageSize,
      },
    },
  );
  return unwrap(data);
}

/** GET /v1/entities/{id}/evidence — content-addressed evidence rows (wire: {entityId, evidence[]}). */
export async function getEntityEvidence(id: string, asOf?: string): Promise<EvidenceView[]> {
  const { data } = await apiClient.get<ApiEnvelope<EvidenceList>>(
    `/v1/entities/${enc(id)}/evidence`,
    { params: { asOf } },
  );
  return unwrap(data).evidence ?? [];
}

// ---- entity browser (F-IS-02 list) --------------------------------------------------------------

export interface EntitySummary {
  id: string;
  typeKey: string;
  status: string;
  validFrom?: string | null;
  sysFrom?: string | null;
}

export interface EntityPage {
  items: EntitySummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface EntityListQuery {
  type?: string;
  idPrefix?: string;
  page?: number;
  pageSize?: number;
}

/** GET /v1/entities — the current entities of the hub (identifiers only; no attrs — PII-safe list). */
export async function listEntities(q: EntityListQuery = {}): Promise<EntityPage> {
  const { data } = await apiClient.get<ApiEnvelope<EntityPage>>('/v1/entities', {
    params: {
      type: q.type || undefined,
      idPrefix: q.idPrefix || undefined,
      page: q.page ?? 1,
      pageSize: q.pageSize ?? 50,
    },
  });
  return unwrap(data);
}
